import { GetStaticProps } from 'next';
import Head from 'next/head';
import * as React from 'react';
import { useState } from 'react';

import categories from '../../categories.json';
import { VerticalPost } from '../../components/Post/lazy';
import { Sidebar } from '../../components/Sidebar/lazy';
import { getAllCategories, getAllPostsFromCategory } from '../../lib/categories';
import { md2Html } from '../../lib/md2Html';
import { getSortedPostsData } from '../../lib/posts';
import { PostEntity } from '../../types';
import { titleBuilder } from '../../utils/titleBuilder';

const Category = ({
    title,
    category,
    posts,
    sidebarPosts
}: {
    title: string;
    category: string;
    posts: PostEntity[];
    sidebarPosts: PostEntity[];
}): JSX.Element => {
    const [amountToShow, setAmountToShow] = useState(8);
    return (
        <>
            <Head>
                <title>{titleBuilder(title)}</title>
            </Head>
            <div className={'page-main'}>
                <section className={'page-container lg:grid max-w-screen-xl'}>
                    <div>
                        <div
                            className={
                                'flex border-b-2 border-gray-light justify-between items-end mb-8 pb-2 text-blue-dark border-dashed'
                            }>
                            <h1 className={'page-title'}>{title}</h1>
                            <span className={'font-medium text-lg'}>
                                {posts.length} Post{posts.length !== 1 && 's'}
                            </span>
                        </div>
                        <ul className={'grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-14'}>
                            {getAllPostsFromCategory(posts, category).map(
                                (post, index) =>
                                    index + 1 <= amountToShow && (
                                        <VerticalPost {...post} key={post.id} />
                                    )
                            )}
                        </ul>
                        <div className={'my-8 w-full text-center'}>
                            {posts.length > amountToShow && (
                                <button
                                    className={'pill-button'}
                                    onClick={() => setAmountToShow((prev) => prev * 2)}>
                                    Carregar Mais Posts
                                </button>
                            )}
                        </div>
                    </div>
                    <Sidebar posts={sidebarPosts} />
                </section>
            </div>
        </>
    );
};

export default Category;
export const getStaticProps: GetStaticProps = async ({
    params
}): Promise<{
    props: {
        title: string;
        posts: PostEntity[];
        sidebarPosts: PostEntity[];
        category: string;
        slug: string;
    };
}> => {
    const [key, { label, slug }] = Object.entries(categories).find(
        ([, { slug }]) => slug === params.id
    );
    const posts = getSortedPostsData();
    const htmlPosts = await Promise.all(
        getAllPostsFromCategory(posts, key).map(async (post) => {
            const content = await md2Html(post.content);
            const summary = await md2Html(post.summary);
            return {
                ...post,
                content,
                summary
            };
        })
    );

    return {
        props: {
            posts: htmlPosts,
            sidebarPosts: posts,
            category: key,
            title: label,
            slug: slug
        }
    };
};

export const getStaticPaths = async (): Promise<{
    paths: { params: { id: string } }[];
    fallback: boolean;
}> => ({
    paths: getAllCategories(),
    fallback: false
});
